import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
  providedIn: 'root'
})
export class CustomAlertService {

  private _transloco = inject(TranslocoService);

  constructor(public snackBar: MatSnackBar) { }

  successMessage(message: string, translate: boolean = true) {
    this.openSnackBar(this.localize(message, translate), 'success-panel')
  }

  private lastFailure = { message: '', at: 0 };

  failedMessage(message: string, timer?, translate: boolean = true) {
    // The same failure is often reported twice (a central handler plus the caller's own).
    const now = Date.now();
    if (message === this.lastFailure.message && now - this.lastFailure.at < 2000) {
      return;
    }
    this.lastFailure = { message, at: now };
    this.openSnackBar(this.localize(message, translate), 'error-panel', timer)
  }

  /** A success that carries a caveat the user must still see (longer than a plain success toast). */
  warningMessage(message: string, timer?, translate: boolean = true) {
    this.openSnackBar(this.localize(message, translate), 'warning-panel', timer)
  }

  openSnackBar(message: string, panelClass: string, timer?) {
    this.snackBar.open(message, '', {
      duration: timer ? timer : panelClass === 'success-panel' ? 1800 : panelClass === 'warning-panel' ? 8000 : 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: [panelClass]
    });
  }

  /**
   * Translate a snackbar message through Transloco. Because this app uses the
   * English source string as the translation key, a missing key returns the input
   * unchanged - so dynamic strings and server-provided messages pass through safely,
   * while any English literal we have keyed is shown in the active language.
   * Callers can pass `translate = false` to force the raw string (e.g. already-localized text).
   */
  private localize(message: string, translate: boolean): string {
    if (!translate || !message) {
      return message;
    }
    return this._transloco.translate(message);
  }
}
